import React, { useEffect, Suspense, lazy } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useNewVersionReload } from './hooks/useNewVersionReload';
import GlobalNotificationBar from './components/common/GlobalNotificationBar';
import PublicNavbar from './components/PublicNavbar';
import HomePage from './components/home/HomePage';
import ProductsPage from './components/products/ProductsPage';
import EffyOrder from './components/products/EffyOrder';
import EffyFuel from './components/products/EffyFuel';
import { EFFY_FUEL_PRODUCT } from './components/products/effyFuelContent';
import ContactPage from './components/contact/ContactPage';
import EffyOrderPricing from './components/pricing/EffyOrderPricing';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import SeoLandingPage from './components/seo/SeoLandingPage';
import FeaturesPage from './components/seo/FeaturesPage';
import FuelFeaturesPage from './components/seo/FuelFeaturesPage';
import GstCalculatorPage from './components/seo/GstCalculatorPage';
import HsnFinderPage from './components/seo/HsnFinderPage';
import { SEO_PAGES, EFFY_ORDER_PRODUCT } from './components/seo/seoPages';
import OrderTrackingLogin from './components/orderTracking/OrderTrackingLogin';
import OrderTrackingSignup from './components/orderTracking/OrderTrackingSignup';
import ForgotPassword from './components/orderTracking/ForgotPassword';
import NotFoundPage from './components/NotFoundPage';
import { isOrderHost, isMarketingHost } from './utils/hostRouting';
import './components/publicTheme.css';

// The logged-in app is large; load it only when someone opens an app route,
// so marketing pages (what Google sees) stay light.
const OrderTrackingApp = lazy(() => import('./components/orderTracking/OrderTrackingApp'));

function AppLoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div className="spinner" style={{ width: 40, height: 40, borderTopColor: 'var(--accent)' }} />
    </div>
  );
}

function isProfileComplete(user) {
  return Boolean(user?.storeName && user?.phoneNumber);
}

// Rendered on effyventure.com when someone hits an order-tracking route
// (e.g. an old bookmark, or a nav click) — hard-redirects to the same path
// on order.effyventure.com since that's a different Vercel "domain" boundary,
// not something React Router can navigate to internally.
function CrossDomainRedirect() {
  useEffect(() => {
    const target = `https://order.effyventure.com${window.location.pathname}${window.location.search}`;
    window.location.replace(target);
  }, []);
  return null;
}

// Loads a new deployment on the user's next page change (needs to sit inside BrowserRouter).
function NewVersionWatcher() {
  useNewVersionReload();
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh' }}><div className="spinner" style={{ width:40, height:40, borderTopColor:'var(--accent)' }} /></div>;
  if (!user) return <Navigate to="/products/order-tracking/login" replace />;

  // Allow the profile page itself through so the user can complete their profile.
  const isOnProfilePage = location.pathname === '/products/order-tracking/profile';
  if (!isProfileComplete(user) && !isOnProfilePage) {
    return <Navigate to="/products/order-tracking/profile" replace />;
  }

  return children;
}

function PublicLayout({ children }) {
  return (
    <div className="pub-root">
      <PublicNavbar />
      {children}
      <footer className="pub-footer">
        <div className="pub-footer__brand">
          <img src="/logo/logo-icon.svg" alt="EffyVenture" className="pub-footer__logo" />
          <span className="pub-footer__name">
            <span className="pub-footer__effy">Effy</span><span className="pub-footer__venture">Venture</span>
          </span>
          <span className="pub-footer__copy">© {new Date().getFullYear()} Effy Tech Venture Private Limited</span>
        </div>
        {/* Crawlable links to every feature page from every public page,
            one row per product plus a row for free tools */}
        <nav className="pub-footer__seo-links" aria-label="Product features">
          <div className="pub-footer__seo-row">
            <Link to={EFFY_ORDER_PRODUCT.featuresPath} className="pub-footer__seo-label">{EFFY_ORDER_PRODUCT.name}:</Link>
            {SEO_PAGES.filter((page) => page.product === EFFY_ORDER_PRODUCT && !page.isGuide).map((page) => (
              <Link key={page.path} to={page.path} className="pub-footer__policy-link">{page.navLabel}</Link>
            ))}
          </div>
          <div className="pub-footer__seo-row">
            <Link to={EFFY_FUEL_PRODUCT.featuresPath} className="pub-footer__seo-label">{EFFY_FUEL_PRODUCT.name}:</Link>
            <Link to={EFFY_FUEL_PRODUCT.path} className="pub-footer__policy-link">Petrol Bunk Software</Link>
            <Link to={EFFY_FUEL_PRODUCT.featuresPath} className="pub-footer__policy-link">Features</Link>
          </div>
          <div className="pub-footer__seo-row">
            <span className="pub-footer__seo-label">Guides:</span>
            {SEO_PAGES.filter((page) => page.isGuide).map((page) => (
              <Link key={page.path} to={page.path} className="pub-footer__policy-link">{page.navLabel}</Link>
            ))}
          </div>
          <div className="pub-footer__seo-row">
            <span className="pub-footer__seo-label">Free tools:</span>
            <Link to="/tools/gst-calculator" className="pub-footer__policy-link">GST Calculator</Link>
            <Link to="/tools/hsn-code-finder" className="pub-footer__policy-link">HSN Code Finder</Link>
          </div>
        </nav>
        <div className="pub-footer__links">
          <Link to="/privacy-policy" className="pub-footer__policy-link">Privacy Policy</Link>
          <span className="pub-footer__sep">·</span>
          <Link to="/terms-of-service" className="pub-footer__policy-link">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  // Evaluated once per full page load (hostname doesn't change client-side),
  // so it's safe to read outside of render state.
  const onOrderHost = isOrderHost();
  const onMarketingHost = isMarketingHost();

  const marketingElement = (children) =>
    onOrderHost ? <Navigate to="/products/order-tracking/login" replace /> : <PublicLayout>{children}</PublicLayout>;

  const orderTrackingElement = (children) =>
    onMarketingHost ? <CrossDomainRedirect /> : children;

  // Unknown address: the order host sends people to the login page,
  // every other host shows a real "not found" page that Google will not index.
  let notFoundElement = <PublicLayout><NotFoundPage /></PublicLayout>;
  if (onOrderHost) {
    notFoundElement = <Navigate to="/products/order-tracking/login" replace />;
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <HelmetProvider>
          <AuthProvider>
            <GlobalNotificationBar />
            <BrowserRouter>
              <NewVersionWatcher />
              <Suspense fallback={<AppLoadingSpinner />}>
              <Routes>
                <Route path="/" element={marketingElement(<HomePage />)} />
                <Route path="/products" element={marketingElement(<ProductsPage />)} />
                <Route path="/products/effy-order" element={marketingElement(<EffyOrder />)} />
                <Route path={EFFY_FUEL_PRODUCT.path} element={marketingElement(<EffyFuel />)} />
                <Route path={EFFY_FUEL_PRODUCT.featuresPath} element={marketingElement(<FuelFeaturesPage />)} />
                <Route path="/contact" element={marketingElement(<ContactPage />)} />
                <Route path="/effy-order-pricing" element={marketingElement(<EffyOrderPricing />)} />
                <Route path="/privacy-policy" element={marketingElement(<PrivacyPolicy />)} />
                <Route path="/terms-of-service" element={marketingElement(<TermsOfService />)} />
                <Route path={EFFY_ORDER_PRODUCT.featuresPath} element={marketingElement(<FeaturesPage />)} />
                <Route path="/tools/gst-calculator" element={marketingElement(<GstCalculatorPage />)} />
                <Route path="/tools/hsn-code-finder" element={marketingElement(<HsnFinderPage />)} />
                {SEO_PAGES.map((page) => (
                  <Route key={page.path} path={page.path} element={marketingElement(<SeoLandingPage page={page} />)} />
                ))}
                <Route path="/products/order-tracking/login" element={orderTrackingElement(<OrderTrackingLogin />)} />
                <Route path="/products/order-tracking/signup" element={orderTrackingElement(<OrderTrackingSignup />)} />
                <Route path="/products/order-tracking/forgot-password" element={orderTrackingElement(<ForgotPassword />)} />
                <Route path="/products/order-tracking/*" element={
                  orderTrackingElement(<ProtectedRoute><OrderTrackingApp /></ProtectedRoute>)
                } />
                <Route path="*" element={notFoundElement} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </HelmetProvider>
      </GoogleOAuthProvider>
  );
}
